import { Badge, Button } from "@l30/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import {
  usePortalPublishedApprovals,
  usePortalPublishedBoardPacks,
  usePortalPublishedInvoices,
  usePortalPublishedMeetings,
  usePortalPublishedSignatures,
  usePortalPublishedSupplierItems,
} from "../../api/portal-published-objects.js";
import { useSignOut, type usePortalMe } from "../../api/portal.js";
import { useSharedDocuments } from "../../api/shared-docs.js";
import { brandForPartner } from "../../lib/partner-brand.js";
import { NAV_SHARED_DOCUMENTS, PORTAL_HELP_TITLE } from "../../lib/portal-copy.js";
import { sumPublishedTotals } from "../../lib/portal-home-model.js";
import { portalSignInPath, tenantScopedPath } from "../../lib/portal-tenant-path.js";
import { PartnerWordmark } from "../brand/partner-wordmark.js";

import { PortalHelpSheet } from "./portal-help-sheet.js";

/**
 * Portal-shell header. Transition33 Figma lock: wordmark + PARTNER PORTAL,
 * gold strip, full programme nav (Calendar / Board / Working groups included).
 */

type Me = NonNullable<ReturnType<typeof usePortalMe>["data"]>;

interface PortalHeaderProps {
  me: Me;
  onReplayOrientation: () => void;
}

export function PortalHeader({ me, onReplayOrientation }: PortalHeaderProps) {
  const navigate = useNavigate();
  const signOut = useSignOut();
  const [helpOpen, setHelpOpen] = useState(false);
  const brand = brandForPartner(me.tenantSlug);
  const isT33 = brand.kind === "transition33";
  const portalLabel =
    me.packageKind === "client_hub" ? "Client Hub" : isT33 ? "Partner Portal" : "Portal";
  const slug = me.tenantSlug;
  const capabilities = new Set(me.capabilities);
  const publishedEnabled = (
    ["signatures", "meetings", "approvals", "invoices", "board_packs", "supplier_workflow"] as const
  ).some((capability) => capabilities.has(capability));

  const reviewsQuery = useSharedDocuments(capabilities.has("document_review"));
  const reviewCount = reviewsQuery.data?.documents.length ?? 0;

  const signaturesQuery = usePortalPublishedSignatures(capabilities.has("signatures"));
  const meetingsQuery = usePortalPublishedMeetings(capabilities.has("meetings"));
  const approvalsQuery = usePortalPublishedApprovals(capabilities.has("approvals"));
  const invoicesQuery = usePortalPublishedInvoices(capabilities.has("invoices"));
  const boardPacksQuery = usePortalPublishedBoardPacks(capabilities.has("board_packs"));
  const supplierItemsQuery = usePortalPublishedSupplierItems(capabilities.has("supplier_workflow"));

  const publishedCount = sumPublishedTotals([
    signaturesQuery.data?.total ?? 0,
    meetingsQuery.data?.total ?? 0,
    approvalsQuery.data?.total ?? 0,
    invoicesQuery.data?.total ?? 0,
    boardPacksQuery.data?.total ?? 0,
    supplierItemsQuery.data?.total ?? 0,
  ]);

  const handleSignOut = () => {
    signOut.mutate(undefined, {
      onSettled: () => {
        void navigate({ href: portalSignInPath(slug) });
      },
    });
  };

  const documentsLabel = isT33 ? "Documents" : NAV_SHARED_DOCUMENTS;

  return (
    <>
      <header className="flex h-14 items-center justify-between bg-surface px-6 md:px-10">
        <div className="flex items-center gap-3">
          <PartnerWordmark brand={brand} size="md" />
          <span className="hidden h-5 w-px bg-border sm:block" aria-hidden />
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-fg-brand">
            {portalLabel}
          </span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div className="hidden text-right sm:block">
            <div className="font-medium text-fg">{me.name}</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setHelpOpen(true)}
            aria-label={PORTAL_HELP_TITLE}
          >
            {PORTAL_HELP_TITLE}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            disabled={signOut.isPending}
            aria-label="Sign out of portal"
          >
            {signOut.isPending ? "Signing out…" : "Sign out"}
          </Button>
        </div>
      </header>
      <PortalHelpSheet
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onReplayOrientation={onReplayOrientation}
        capabilities={me.capabilities}
      />
      <div
        aria-hidden="true"
        className={isT33 ? "t33-gold-strip" : "h-1 bg-accent"}
        style={!isT33 && me.accentColor ? { backgroundColor: me.accentColor } : undefined}
      />
      <nav
        className="flex gap-0.5 overflow-x-auto border-b border-border bg-surface px-4 py-2 text-sm md:px-10"
        aria-label="Portal"
      >
        <PortalNavLink to={tenantScopedPath(slug, "/portal")} exact goldActive={isT33}>
          Overview
        </PortalNavLink>
        {capabilities.has("meetings") && (
          <PortalNavLink to={tenantScopedPath(slug, "/portal/calendar")} goldActive={isT33}>
            Calendar
          </PortalNavLink>
        )}
        {capabilities.has("board_workspace") && (
          <PortalNavLink to={tenantScopedPath(slug, "/portal/board")} goldActive={isT33}>
            Board
          </PortalNavLink>
        )}
        {capabilities.has("working_groups") && (
          <PortalNavLink to={tenantScopedPath(slug, "/portal/working-groups")} goldActive={isT33}>
            Committees &amp; working groups
          </PortalNavLink>
        )}
        {capabilities.has("programmes") && (
          <PortalNavLink to={tenantScopedPath(slug, "/portal/programmes")} goldActive={isT33}>
            Programmes
          </PortalNavLink>
        )}
        {capabilities.has("engagement_plan") && (
          <PortalNavLink to={tenantScopedPath(slug, "/portal/plan")} goldActive={isT33}>
            Engagement plan
          </PortalNavLink>
        )}
        {capabilities.has("documents") && (
          <PortalNavLink to={tenantScopedPath(slug, "/portal/vault")} goldActive={isT33}>
            {documentsLabel}
          </PortalNavLink>
        )}
        {capabilities.has("projects") && (
          <PortalNavLink to={tenantScopedPath(slug, "/portal/project-plan")} goldActive={isT33}>
            Project plan
          </PortalNavLink>
        )}
        {publishedEnabled && (
          <PortalNavLink
            to={tenantScopedPath(slug, "/portal/published")}
            badge={publishedCount > 0 ? publishedCount : undefined}
            goldActive={isT33}
          >
            Published items
          </PortalNavLink>
        )}
        {capabilities.has("document_review") && (
          <PortalNavLink
            to={tenantScopedPath(slug, "/portal/reviews")}
            badge={reviewCount > 0 ? reviewCount : undefined}
            goldActive={isT33}
          >
            For review
          </PortalNavLink>
        )}
      </nav>
    </>
  );
}

function PortalNavLink({
  to,
  children,
  exact = false,
  badge,
  goldActive = false,
}: {
  to: string;
  children: ReactNode;
  exact?: boolean;
  badge?: number;
  goldActive?: boolean;
}) {
  return (
    <Link
      to={to as "/t/$tenantSlug/portal"}
      activeOptions={{ exact }}
      activeProps={{
        className: goldActive
          ? "border-b-2 border-accent font-semibold text-fg-brand"
          : "border-b-2 border-fg-brand font-semibold text-fg-brand",
      }}
      inactiveProps={{ className: "border-b-2 border-transparent text-fg-muted hover:text-fg" }}
      className="inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-2 transition-colors md:px-3"
    >
      {children}
      {badge !== undefined && badge > 0 && (
        <Badge variant="warning" className="min-w-5 justify-center px-1.5 py-0 text-xs">
          {badge}
        </Badge>
      )}
    </Link>
  );
}
